import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { BlueprintSelector } from './BlueprintSelector';
import { ResultsTables } from './ResultsTables';
import { bomService } from '@/services';
import type { ItemBlueprint, BomResult } from '@/types';

export const Calculator: React.FC = () => {
  const [initialized, setInitialized] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<ItemBlueprint | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<BomResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize BOM service on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        await bomService.initialize();
        setInitialized(true);
      } catch (err) {
        setError('Failed to initialize calculator. Please refresh cache.');
        console.error(err);
      }
    };

    initialize();
  }, []);

  // Re-calculate with fresh prices when pricesRefreshed event is fired
  const recalculateWithFreshPrices = useCallback(async () => {
    // Only recalculate if there's an existing result to refresh
    // (prices are fetched lazily during BOM calculation)
    if (!selectedBlueprint || !result) return;

    try {
      setCalculating(true);
      const bomResult = await bomService.calculateWithUserSkills(selectedBlueprint, quantity);
      setResult(bomResult);
    } catch (err) {
      console.error('Failed to refresh prices:', err);
    } finally {
      setCalculating(false);
    }
  }, [selectedBlueprint, quantity, result]);

  // Listen for price refresh events
  useEffect(() => {
    const handlePricesRefreshed = () => {
      recalculateWithFreshPrices();
    };

    window.addEventListener('pricesRefreshed', handlePricesRefreshed);
    return () => {
      window.removeEventListener('pricesRefreshed', handlePricesRefreshed);
    };
  }, [recalculateWithFreshPrices]);

  const handleCalculate = async () => {
    if (!selectedBlueprint) {
      setError('Please select a blueprint');
      return;
    }

    if (quantity < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    try {
      setCalculating(true);
      setError(null);

      const bomResult = await bomService.calculateWithUserSkills(selectedBlueprint, quantity);
      setResult(bomResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to calculate BOM');
      console.error('Calculation error:', err);
    } finally {
      setCalculating(false);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      setQuantity(value);
    }
  };

  if (!initialized) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography>Initializing calculator...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Input Card */}
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h5">Manufacturing Calculator</Typography>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <BlueprintSelector
              value={selectedBlueprint}
              onChange={(bp) => {
                setSelectedBlueprint(bp);
                setResult(null); // Clear results when blueprint changes
              }}
              disabled={calculating}
            />

            <TextField
              label="Quantity to Manufacture"
              type="number"
              value={quantity}
              onChange={handleQuantityChange}
              disabled={calculating}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              helperText="How many items do you want to build?"
            />

            <Button
              variant="contained"
              size="large"
              startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
              onClick={handleCalculate}
              disabled={!selectedBlueprint || calculating}
              fullWidth
            >
              {calculating ? 'Calculating...' : 'Calculate Materials'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Results */}
      {result && <ResultsTables result={result} />}
    </Stack>
  );
};
